import { OptionDefinition } from '@amzn/awsui-components-react/polaris/internal/components/option/interfaces';
import { PieData } from 'plotly.js';

import { AWS_ORANGE_COLOR, BLUE_COLOR, GREEN_COLOR, PURPLE_COLOR} from '../../common/colors';
import { constants } from '../../common/Localization';
import { DistributionTime, Resource } from './interfaces';

export const MIGRATION_TOOLS_SCHEDULE = 'migrationToolsAndSchedule';
export const MIGRATION_TOOLS_SCHEDULE_CAPITALIZED = 'MigrationToolsAndSchedule';
export const CUSTOMER_LOCATION = 'customerLocation';
export const MIGRATION_RESOURCE_COSTS = 'MigrationResourceCosts';
export const MIGRATION_SPRINT_TEAM = 'MigrationSprintTeamComposition';
export const DATABASE_SPRINT_TEAM = 'DatabaseSprintTeamComposition';
export const regionalConfigurationCategories = 
        [MIGRATION_RESOURCE_COSTS, MIGRATION_SPRINT_TEAM, MIGRATION_TOOLS_SCHEDULE_CAPITALIZED, DATABASE_SPRINT_TEAM];
export const RESOURCE_LABELS = [constants.PARTNER, constants.AWS, constants.CUSTOMER, constants.TOOLS_TRAINING];
export const chartColors = {
    partner: GREEN_COLOR,
    aws: AWS_ORANGE_COLOR,
    customer: PURPLE_COLOR,
    tools: BLUE_COLOR
};
export const legendLayout = {
    x: 0,
    y: -0.5,
};

export const margins = {
    t: 60,
    r: 0,
    l: 0,
    b: 0,
    pad: 0
};
export const commonGraphData: Partial<PieData> = {
    type: 'pie',
    labels: RESOURCE_LABELS,
    hoverinfo: 'label+text',
    marker: {
        colors: [chartColors.partner, chartColors.aws, chartColors.customer, chartColors.tools],
    },
    visible: true,
};
export const RATE = 'rate';
export const MIGRATION_RESOURCE_CAP = 1000;

export enum SprintResourceType {
    SERVER = 'server',
    DATABASE = 'database',
}
export const resources: Resource[] = [
    {name: constants.CUSTOMER_SENIOR_RESOURCES, countIdentifier: '{RType}CustomerSrResources', costIdentifier: 'customerSeniorCost'},
    {name: constants.CUSTOMER_RESOURCES, countIdentifier: '{RType}CustomerResources', costIdentifier: 'customerCost'},
    {name: constants.CUSTOMER_SENIOR_OFFSHORE_RESOURCES, countIdentifier: '{RType}CustomerSrOffshoreResources', costIdentifier: 'customerSeniorOffshoreCost'},
    {name: constants.CUSTOMER_OFFSHORE_RESOURCES, countIdentifier: '{RType}CustomerOffshoreResources', costIdentifier: 'customerOffshoreCost'},
    {name: constants.PARTNER_SENIOR_RESOURCES, countIdentifier: '{RType}PartnerSrResources', costIdentifier: 'partnerSeniorCost'},
    {name: constants.PARTNER_RESOURCES, countIdentifier: '{RType}PartnerResources', costIdentifier: 'partnerCost'},
    {name: constants.PARTNER_SENIOR_OFFSHORE_RESOURCES, countIdentifier: '{RType}PartnerSrOffshoreResources', costIdentifier: 'partnerSeniorOffshoreCost'},
    {name: constants.PARTNER_OFFSHORE_RESOURCES, countIdentifier: '{RType}PartnerOffshoreResources', costIdentifier: 'partnerOffshoreCost'},
    {name: constants.AWS_SENIOR_RESOURCES, countIdentifier: '{RType}AWSSrResources', costIdentifier: 'awsSeniorCost'},
    {name: constants.AWS_RESOURCES, countIdentifier: '{RType}AWSResources', costIdentifier: 'awsCost'},
    {name: constants.AWS_SENIOR_OFFSHORE_RESOURCES, countIdentifier: '{RType}AWSSrOffshoreResources', costIdentifier: 'awsSeniorOffshoreCost'},
    {name: constants.AWS_OFFSHORE_RESOURCES, countIdentifier: '{RType}AWSOffshoreResources', costIdentifier: 'awsOffshoreCost'}
];
/**
 * Return the Rate Cap for a particular resource
 * @param  {string} resource
 * @returns number which is the current rate cap for the particular resource
 */
export const getRateCapByResource = (resource: string): number => {
    switch (resource) {
        case constants.CUSTOMER_SENIOR_RESOURCES:
        case constants.CUSTOMER_RESOURCES:
        case constants.CUSTOMER_SENIOR_OFFSHORE_RESOURCES:
        case constants.CUSTOMER_OFFSHORE_RESOURCES:
            return 250;
        case constants.PARTNER_OFFSHORE_RESOURCES:
        case constants.PARTNER_SENIOR_OFFSHORE_RESOURCES:
            return 300;
        default:
            return 500;
    }
};

export const DEFAULT_MIGRATION_TRANCHE_IN_MONTHS = 3;
export const DEFAULT_MIGRATION_MINIMUM_LENGTH_IN_MONTHS = 6;
export const completionPlanDistributionTime: DistributionTime[] = [
    {label: constants.QUARTER, id: 'quarterly', unitInMonths: 3},
    {label: constants.MONTH, id: 'monthly', unitInMonths: 1}
];

export const getMigrationStrategyOptions = (relocatedSupported: boolean): OptionDefinition[] => {
    return [
        { label: 'Retire', value: 'Retire' },
        { label: 'Retain', value: 'Retain' },
        { label: 'Relocate', value: 'Relocate', disabled: !relocatedSupported},
        { label: 'Repurchase', value: 'Repurchase' },
        { label: 'Rehost', value: 'Rehost' },
        { label: 'Replatform', value: 'Replatform' },
        { label: 'Rearchitect', value: 'Rearchitect' },
        { label: 'Unknown', value: 'Unknown' },
        { label: 'Undo Manual Update', value: 'UndoManualUpdate'}
    ];
};

export const migrationStrategyResourceName = 'aws-migration-strategy-assessment';
export const serverResourceName = 'servers';
export const applicationResourceName = 'applications';
